<template>
  <section class="section">
    <div class="container">
      <div class="is-flex is-justify-content-end">
        <b-icon
          icon="account-circle"
          size="is-large"
          type="is-primary"
        ></b-icon>
        <p class="is-size-3 ml-3">
          {{ $store.state.users.user }}
        </p>
      </div>

      <!-- Show enforcement message if password change is required -->
      <div v-if="isPasswordChangeEnforced" class="notification is-warning mb-4">
        <div class="content">
          <h4 class="title is-5">
            {{ $t("password_change.enforcement_title") }}
          </h4>
          <p>
            {{
              passwordChangeMessage ||
              $t("password_change.enforcement_messages.generic")
            }}
          </p>
          <p class="has-text-weight-semibold">
            {{ $t("password_change.enforcement_notice") }}
          </p>
        </div>
      </div>

      <LocaleSwitcher></LocaleSwitcher>
      <div class="columns is-centered is-mobile">
        <div class="column is-8-mobile">
          <div class="box has-background-eurotext-light has-text-white-bis">
            <h2
              class="title is-6 has-text-white-bis has-text-weight-bold has-text-centered"
            >
              {{
                isPasswordChangeEnforced
                  ? $t("password_change.required_change_title")
                  : $t("password_change.change_user_password_title")
              }}
            </h2>

            <!-- Show password policy if available -->
            <div v-if="passwordPolicy" class="notification is-info mb-4">
              <h5 class="title is-6">
                {{ $t("password_change.policy_requirements") }}
              </h5>
              <ul>
                <li v-if="passwordPolicy.minLength">
                  {{
                    $t("password_change.min_length", {
                      length: passwordPolicy.minLength,
                    })
                  }}
                </li>
                <li v-if="passwordPolicy.requireUppercase">
                  {{ $t("password_change.require_uppercase") }}
                </li>
                <li v-if="passwordPolicy.requireLowercase">
                  {{ $t("password_change.require_lowercase") }}
                </li>
                <li v-if="passwordPolicy.requireNumbers">
                  {{ $t("password_change.require_numbers") }}
                </li>
                <li v-if="passwordPolicy.requireSpecialChars">
                  {{
                    passwordPolicy.allowedSpecialChars
                      ? $t("password_change.require_special_chars_with_list", {
                          chars: passwordPolicy.allowedSpecialChars,
                        })
                      : $t("password_change.require_special_chars")
                  }}
                </li>
              </ul>
            </div>

            <PasswordChangeForm
              @change-password="changeUserPassword"
              :is-enforced="isPasswordChangeEnforced"
              :password-policy="passwordPolicy"
              :ask-old-password="shouldAskOldPassword"
              label-class="has-text-weight-normal has-text-white-bis"
            ></PasswordChangeForm>

            <b-loading
              :is-full-page="false"
              v-model="isLoadingUserPassword"
            ></b-loading>
          </div>
        </div>
      </div>

      <div class="is-relative is-pulled-right mt-4 is-size-5">
        v{{ version }}
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import PasswordChangeForm from "@/components/PasswordChangeForm";
import { userService } from "@/services";
import {
  RESULT_CODE_PASSWORD_NOT_COMPLIANT,
  RESULT_CODE_PASSWORD_REUSED,
} from "@/helpers/constants/authConstants";

export default {
  components: {
    LocaleSwitcher,
    PasswordChangeForm,
  },
  data() {
    return {
      isLoadingUserPassword: false,
      version: process.env.VUE_APP_VERSION,
      standardPasswordPolicy: null, // For standard password changes
    };
  },
  computed: {
    ...mapState({
      classes: (state) => state.menu.data.classes,
    }),
    ...mapGetters("users", [
      "isPasswordChangeRequired",
      "getPasswordChangeContext",
      "isPasswordChangeInProgress",
      "getPasswordChangeError",
    ]),
    isPasswordChangeEnforced() {
      return this.isPasswordChangeRequired;
    },
    passwordChangeMessage() {
      return this.getPasswordChangeContext?.message;
    },
    passwordPolicy() {
      // For enforced password changes, use policy from login context
      if (this.isPasswordChangeEnforced) {
        return this.getPasswordChangeContext?.passwordPolicy;
      }
      // For standard password changes, use loaded policy
      return this.standardPasswordPolicy;
    },
    shouldAskOldPassword() {
      // For enforced password changes, check if it's a temporary password scenario
      if (this.isPasswordChangeEnforced) {
        const resultCode = this.getPasswordChangeContext?.resultCode;
        // Don't ask for old password if it's a temporary password (result code -8002)
        return resultCode !== "TEMPORARY_PASSWORD" && resultCode !== -8002;
      }
      // For voluntary password changes, always ask for old password
      return true;
    },
  },
  async mounted() {
    // Load password policy for standard password changes (not enforced)
    if (!this.isPasswordChangeEnforced) {
      try {
        this.standardPasswordPolicy = await userService.getPasswordPolicy();
      } catch (error) {
        console.warn("Could not load password policy:", error);
        // Use default policy if loading fails
        this.standardPasswordPolicy = {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: true,
        };
      }
    }
  },
  methods: {
    changeUserPassword(oldPassword, newPassword) {
      console.log(oldPassword, newPassword);
      this.isLoadingUserPassword = true;

      // Check if this is an enforced password change
      if (this.isPasswordChangeEnforced) {
        // Use enforced password change method
        this.completeEnforcedPasswordChange(oldPassword, newPassword);
      } else {
        // Use regular password change method
        this.performRegularPasswordChange(oldPassword, newPassword);
      }
    },

    completeEnforcedPasswordChange(oldPassword, newPassword) {
      const username = this.$store.state.users.user;

      this.$store
        .dispatch("users/changePasswordDuringLogin", {
          username,
          oldPassword,
          newPassword,
        })
        .then(() => {
          this.$buefy.toast.open({
            message: this.$t("messages.password_updated_success"),
            type: "is-success",
          });
          // Navigation will be handled by the store action
        })
        .catch((error) => {
          // Handle password change errors with specific messages
          this.showPasswordChangeError(error);
        })
        .finally(() => {
          this.isLoadingUserPassword = false;
        });
    },

    performRegularPasswordChange(oldPassword, newPassword) {
      userService
        .updateUserPassword(oldPassword, newPassword)
        .then((data) => {
          if (data) {
            this.$buefy.toast.open({
              message: this.$t("messages.password_updated_success"),
              type: "is-success",
            });
          } else {
            this.$buefy.toast.open({
              message: this.$t("error.wrong_password"),
              type: "is-danger",
            });
          }
        })
        .catch((error) => {
          // Handle password change errors with specific messages
          this.showPasswordChangeError(error);
        })
        .finally(() => {
          this.isLoadingUserPassword = false;
        });
    },

    // Shared method to show password change error messages
    showPasswordChangeError(error) {
      let errorMessage;
      const errorCode = error.response?.data?.code || error.code;

      switch (errorCode) {
        case RESULT_CODE_PASSWORD_NOT_COMPLIANT:
          errorMessage = this.$t("password_change.validation.policy_not_met");
          break;
        case RESULT_CODE_PASSWORD_REUSED:
          errorMessage = this.$t("password_change.validation.password_reused");
          break;
        default:
          errorMessage =
            error.response?.data?.internalMessage ||
            error.message ||
            this.$t("password_change.validation.password_change_failed");
      }

      this.$buefy.toast.open({
        message: errorMessage,
        type: "is-danger",
        duration: 5000,
      });
    },
  },

  // Navigation guard to prevent leaving during enforced password change
  beforeRouteLeave(to, from, next) {
    // If password change is required and not completed, prevent navigation
    if (this.isPasswordChangeRequired && !this.isPasswordChangeInProgress) {
      // Show confirmation dialog
      this.$buefy.dialog.confirm({
        title: this.$t("password_change.leave_confirmation_title"),
        message: this.$t("password_change.leave_confirmation_message"),
        confirmText: this.$t("password_change.stay_and_change"),
        cancelText: this.$t("password_change.logout"),
        type: "is-warning",
        hasIcon: true,
        onConfirm: () => {
          // Stay on the page
          next(false);
        },
        onCancel: () => {
          // Logout and allow navigation
          this.$store.dispatch("users/logout");
          next();
        },
      });
    } else {
      // Allow normal navigation
      next();
    }
  },
};
</script>

<style></style>
